import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { SiteShell } from "@/components/site/SiteShell";
import { buildHead } from "@/lib/seo-head";
import { Section } from "@/components/site/Blocks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { runStrAnalysisAsLO, listStrReports } from "@/lib/bnbcalc.functions";
import { normalizeStrReport, fmtUsd, fmtPct, fmtOcc, STR_SCENARIO_LABELS } from "@/lib/str-report";

export const Route = createFileRoute("/lo/str-report")({
  ssr: false,
  head: () =>
    buildHead({
      title: "Loan Officer Short-Term Rental Analyzer | Internal Tools",
      description: "Internal loan officer tool to model short-term rental revenue for acquisition, arbitrage, owned, and co-host scenarios.",
      url: "/lo/str-report",
      noindex: true,
      localBusiness: false,
    }),
  component: StrToolPage,
});

const SCENARIOS = [
  { id: "buy", label: "Acquisition (buy)", hint: "Investor is purchasing the property." },
  { id: "arb", label: "Rental arbitrage", hint: "Investor leases the property and sublets it short-term." },
  { id: "owned", label: "Already owned (refi)", hint: "Investor owns it; model current mortgage terms." },
  { id: "cohost", label: "Co-host / management", hint: "Investor manages someone else's property." },
] as const;

type ScenarioId = (typeof SCENARIOS)[number]["id"];

type RecentRow = {
  id: string;
  created_at: string;
  client_name: string | null;
  client_email: string | null;
  full_address: string;
  analysis_type: string;
  purchase_price_usd: number | null;
};

function StrToolPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const runAnalysis = useServerFn(runStrAnalysisAsLO);
  const loadRecent = useServerFn(listStrReports);

  const [scenario, setScenario] = useState<ScenarioId>("buy");
  const [fullAddress, setFullAddress] = useState("");
  const [bedrooms, setBedrooms] = useState(3);
  const [bathrooms, setBathrooms] = useState(2);
  const [accomodates, setAccomodates] = useState(6);
  const [purchasePrice, setPurchasePrice] = useState<number | "">("");
  const [monthlyRent, setMonthlyRent] = useState<number | "">("");
  const [squareFeet, setSquareFeet] = useState<number | "">("");
  const [rate, setRate] = useState<number | "">("");
  const [downPct, setDownPct] = useState<number | "">("");
  const [mortgageLength, setMortgageLength] = useState<number | "">("");
  const [yearsRemaining, setYearsRemaining] = useState<number | "">("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [pushToCrm, setPushToCrm] = useState(true);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [underwriting, setUnderwriting] = useState<any | null>(null);
  const [recent, setRecent] = useState<RecentRow[]>([]);

  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(async ({ data }) => {
      if (!active) return;
      if (!data.user) {
        navigate({ to: "/auth", search: { redirect: "/lo/str-report" }, replace: true });
        return;
      }
      try {
        const rows = (await loadRecent({ data: undefined })) as RecentRow[];
        if (active) setRecent(rows);
      } catch {
        /* non-fatal */
      } finally {
        if (active) setChecking(false);
      }
    });
    return () => { active = false; };
  }, [loadRecent, navigate]);

  const num = (v: number | "") => (v === "" ? undefined : Number(v));

  async function submit() {
    if (fullAddress.trim().length < 5) {
      toast.error("Enter the full property address.");
      return;
    }
    if (scenario === "arb" && !monthlyRent) {
      toast.error("Arbitrage scenarios need the monthly lease amount.");
      return;
    }
    if ((scenario === "buy" || scenario === "owned") && !purchasePrice) {
      toast.error(scenario === "buy" ? "Enter the purchase price." : "Enter the current property value.");
      return;
    }
    setRunning(true);
    setResult(null);
    setAiSummary(null);
    setUnderwriting(null);
    try {
      const res = await runAnalysis({
        data: {
          analysisType: scenario,
          fullAddress: fullAddress.trim(),
          bedrooms,
          bathrooms,
          accomodates,
          purchasePriceUSD: num(purchasePrice),
          monthlyRentUSD: num(monthlyRent),
          squareFeet: num(squareFeet),
          interestRatePercentage: num(rate),
          downPaymentPercentage: num(downPct),
          mortgageLength: num(mortgageLength),
          yearsRemainingOnMortgage: num(yearsRemaining),
          clientName: clientName.trim() || undefined,
          clientEmail: clientEmail.trim() || undefined,
          notes: notes.trim() || undefined,
          pushToCrm,
        },
      });
      setResult(res.data);
      setAiSummary((res as { aiSummary?: string | null }).aiSummary ?? null);
      setUnderwriting((res as { underwriting?: unknown }).underwriting ?? null);
      toast.success(pushToCrm ? "Analysis saved and pushed to the client profile." : "Analysis saved.");
      try {
        setRecent((await loadRecent({ data: undefined })) as RecentRow[]);
      } catch { /* non-fatal */ }
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Analysis failed.");
    } finally {
      setRunning(false);
    }
  }

  if (checking) {
    return (
      <SiteShell>
        <Section>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your access…
          </div>
        </Section>
      </SiteShell>
    );
  }

  const n = result ? normalizeStrReport(result) : null;
  const showMortgage = scenario === "owned";
  const showPrice = scenario === "buy" || scenario === "owned";
  const showRent = scenario === "arb";

  return (
    <SiteShell>
      <Section>
        <h1 className="text-2xl font-black sm:text-3xl">Short-term rental analyzer</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Model projected short-term rental performance for a client scenario. Results save to the client's file and can be
          pushed straight to their profile so it's available for the pricing call.
        </p>

        {/* Scenario picker */}
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {SCENARIOS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setScenario(s.id)}
              className={`rounded-xl border p-4 text-left transition ${
                scenario === s.id ? "border-brand bg-brand/5 ring-2 ring-brand/30" : "border-border bg-card hover:border-brand/40"
              }`}
            >
              <div className="text-sm font-bold">{s.label}</div>
              <div className="mt-1 text-xs text-muted-foreground">{s.hint}</div>
            </button>
          ))}
        </div>

        {/* Inputs */}
        <div className="mt-6 grid gap-4 rounded-2xl border border-border bg-card p-5 sm:grid-cols-2 lg:grid-cols-3">
          <div className="sm:col-span-2 lg:col-span-3">
            <Label htmlFor="addr">Property address</Label>
            <Input id="addr" value={fullAddress} onChange={(e) => setFullAddress(e.target.value)} placeholder="123 Main St, Orlando, FL 32801" />
          </div>
          <Field label="Bedrooms" value={bedrooms} onChange={(v) => setBedrooms(Number(v) || 0)} />
          <Field label="Bathrooms" value={bathrooms} onChange={(v) => setBathrooms(Number(v) || 0)} step="0.5" />
          <Field label="Sleeps" value={accomodates} onChange={(v) => setAccomodates(Number(v) || 1)} />
          {showPrice ? (
            <Field
              label={scenario === "buy" ? "Purchase price ($)" : "Current property value ($)"}
              value={purchasePrice}
              onChange={(v) => setPurchasePrice(v === "" ? "" : Number(v))}
            />
          ) : null}
          {showRent ? (
            <Field label="Monthly lease cost ($)" value={monthlyRent} onChange={(v) => setMonthlyRent(v === "" ? "" : Number(v))} />
          ) : null}
          <Field label="Square feet (optional)" value={squareFeet} onChange={(v) => setSquareFeet(v === "" ? "" : Number(v))} />
          {showMortgage ? (
            <>
              <Field label="Interest rate (%)" value={rate} onChange={(v) => setRate(v === "" ? "" : Number(v))} step="0.125" />
              <Field label="Original down payment (%)" value={downPct} onChange={(v) => setDownPct(v === "" ? "" : Number(v))} />
              <Field label="Mortgage term (years)" value={mortgageLength} onChange={(v) => setMortgageLength(v === "" ? "" : Number(v))} />
              <Field label="Years remaining" value={yearsRemaining} onChange={(v) => setYearsRemaining(v === "" ? "" : Number(v))} />
            </>
          ) : null}
          <div>
            <Label htmlFor="cn">Client name</Label>
            <Input id="cn" value={clientName} onChange={(e) => setClientName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="ce">Client email</Label>
            <Input id="ce" type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="nt">Notes</Label>
            <Input id="nt" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Scenario context for the file" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={pushToCrm} onChange={(e) => setPushToCrm(e.target.checked)} className="h-4 w-4" />
              Push results to the client profile
            </label>
            <Button onClick={submit} disabled={running}>
              {running ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running analysis…</> : <><Sparkles className="mr-2 h-4 w-4" /> Run analysis</>}
            </Button>
          </div>
        </div>

        {/* Results */}
        {n ? (
          <div className="mt-8 rounded-2xl border border-brand-gold/40 bg-brand-gold/5 p-5">
            <div className="text-[11px] font-black uppercase tracking-widest text-brand">
              {SCENARIOS.find((s) => s.id === scenario)?.label} — {fullAddress}
            </div>

            {aiSummary || underwriting ? (
              <div className="mt-4 rounded-xl border border-brand/30 bg-background p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-black uppercase tracking-widest text-brand">
                    AI underwriting summary
                  </span>
                  {underwriting?.passesAtP50 != null ? (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                        underwriting.passesAtP50
                          ? "bg-emerald-500/15 text-emerald-700"
                          : "bg-amber-500/15 text-amber-700"
                      }`}
                    >
                      {underwriting.passesAtP50 ? "Clears minimum DSCR (median case)" : "Below minimum DSCR (median case)"}
                    </span>
                  ) : null}
                </div>

                {underwriting ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <Stat label="DSCR — conservative" value={underwriting.dscrNet?.p25 ?? "—"} />
                    <Stat label="DSCR — median" value={underwriting.dscrNet?.p50 ?? "—"} />
                    <Stat label="DSCR — long-term lease" value={underwriting.dscrLongTerm ?? "—"} />
                    <Stat label="Monthly PITIA" value={fmtUsd(underwriting.monthlyPitiaUSD)} />
                    <Stat label="Loan amount" value={fmtUsd(underwriting.loanAmountUSD)} />
                    <Stat label="LTV used" value={`${underwriting.ltvUsed ? Math.round(underwriting.ltvUsed * 100) : "—"}%`} />
                    <Stat label="Rate assumed" value={`${underwriting.ratePct ?? "—"}%`} />
                    <Stat label="Max loan at min DSCR" value={fmtUsd(underwriting.maxLoanAtMinDscrUSD)} />
                  </div>
                ) : null}

                {aiSummary ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{aiSummary}</p>
                ) : null}

                {underwriting?.assumptions?.length ? (
                  <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                    Assumptions: {underwriting.assumptions.join(" · ")}. Figures are illustrative only and subject to
                    underwriting, appraisal, and final pricing.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-background">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2 text-left font-semibold">Scenario</th>
                    <th className="px-3 py-2 text-right font-semibold">Annual revenue</th>
                    <th className="px-3 py-2 text-right font-semibold">ADR</th>
                    <th className="px-3 py-2 text-right font-semibold">Occupancy</th>
                  </tr>
                </thead>
                <tbody>
                  {(["p25", "p50", "p75", "p90"] as const).map((k) => (
                    <tr key={k} className={`border-b border-border/60 last:border-0 ${k === "p50" ? "bg-brand/5 font-semibold" : ""}`}>
                      <td className="px-3 py-2">{STR_SCENARIO_LABELS[k]}</td>
                      <td className="px-3 py-2 text-right">{fmtUsd(n.revenue[k])}</td>
                      <td className="px-3 py-2 text-right">{fmtUsd(n.adr[k])}</td>
                      <td className="px-3 py-2 text-right">{fmtOcc(n.occupancy[k])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Year-one cash flow" value={fmtUsd(n.yearOne.cashFlowUSD)} />
              <Stat label="Cap rate (Y1)" value={fmtPct(n.yearOne.capRatePercentage, 2)} />
              <Stat label="Cash-on-cash (Y1)" value={fmtPct(n.yearOne.cashOnCashPercentage, 2)} />
              <Stat label="ROI (Y1)" value={fmtPct(n.yearOne.returnOnInvestmentPercentage, 2)} />
              <Stat label="Principal paydown (Y1)" value={fmtUsd(n.yearOne.principalPaydownUSD)} />
              <Stat label="Appreciation (Y1)" value={fmtUsd(n.yearOne.appreciationUSD)} />
              <Stat label="Long-term rent" value={n.ltrPerMonthUSD != null ? `${fmtUsd(n.ltrPerMonthUSD)}/mo` : "—"} />
              <Stat label="STR premium vs. lease" value={n.strVsLtrAnnualDeltaUSD != null ? `${fmtUsd(n.strVsLtrAnnualDeltaUSD)}/yr` : "—"} />
            </div>

            {n.comparables.length ? (
              <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-background">
                <table className="w-full min-w-[620px] text-sm">
                  <thead>
                    <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2 text-left font-semibold">Comparable listing</th>
                      <th className="px-3 py-2 text-right font-semibold">Beds / sleeps</th>
                      <th className="px-3 py-2 text-right font-semibold">ADR</th>
                      <th className="px-3 py-2 text-right font-semibold">Occupancy</th>
                      <th className="px-3 py-2 text-right font-semibold">Annual revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {n.comparables.slice(0, 10).map((c, i) => (
                      <tr key={`${c.url ?? c.name}-${i}`} className="border-b border-border/60 last:border-0">
                        <td className="px-3 py-2">
                          {c.url ? (
                            <a href={c.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-medium text-brand hover:underline">
                              {c.name} <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : c.name}
                          {c.distanceMiles != null ? <span className="block text-[11px] text-muted-foreground">{c.distanceMiles.toFixed(1)} mi away</span> : null}
                        </td>
                        <td className="px-3 py-2 text-right">{c.bedrooms ?? "—"} / {c.accommodates ?? "—"}</td>
                        <td className="px-3 py-2 text-right">{fmtUsd(c.averageDailyRateUSD)}</td>
                        <td className="px-3 py-2 text-right">{c.occupancyRatePercentage != null ? `${Math.round(c.occupancyRatePercentage)}%` : "—"}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmtUsd(c.annualRevenueUSD)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Recent */}
        {recent.length ? (
          <div className="mt-10">
            <h2 className="text-lg font-bold">Recent analyses</h2>
            <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2 text-left font-semibold">Date</th>
                    <th className="px-3 py-2 text-left font-semibold">Client</th>
                    <th className="px-3 py-2 text-left font-semibold">Property</th>
                    <th className="px-3 py-2 text-left font-semibold">Scenario</th>
                    <th className="px-3 py-2 text-right font-semibold">Price / value</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((row) => (
                    <tr key={row.id} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-2">{new Date(row.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2">{row.client_name || row.client_email || "—"}</td>
                      <td className="px-3 py-2">{row.full_address}</td>
                      <td className="px-3 py-2">{SCENARIOS.find((s) => s.id === row.analysis_type)?.label ?? row.analysis_type}</td>
                      <td className="px-3 py-2 text-right">{fmtUsd(row.purchase_price_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </Section>
    </SiteShell>
  );
}

function Field({
  label, value, onChange, step,
}: { label: string; value: number | ""; onChange: (v: string) => void; step?: string }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-bold">{value}</div>
    </div>
  );
}
